import { Dispatch, SetStateAction } from 'react';
import { SummaryFeedbackType } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { UpdateSummaryFeedbackRequest } from '@weave/schema-gen-ts/dist/schemas/fax/summary/v1/fax_summary.pb';
import { FaxSummaryV1 } from '@frontend/api-fax-summary';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  FormRow,
  Modal,
  ModalControlModalProps,
  Text,
  TextareaField,
  useAlert,
  useForm,
} from '@frontend/design-system';

type Props = {
  feedbackModalProps: ModalControlModalProps;
  setFeedbackRequest: Dispatch<SetStateAction<UpdateSummaryFeedbackRequest | undefined>>;
  feedbackRequest?: UpdateSummaryFeedbackRequest;
};

export const FeedbackModal = ({ feedbackModalProps, setFeedbackRequest, feedbackRequest }: Props) => {
  const { t } = useTranslation('fax');
  const alert = useAlert();

  const { getFieldProps, values } = useForm({
    fields: {
      feedback: { type: 'text', value: '' },
      allowContact: { type: 'checkbox', value: false },
    },
  });

  const handleModalClose = () => {
    feedbackModalProps.onClose();
  };

  const { mutate: submitFeedback, isLoading: isSubmittingFeedback } =
    FaxSummaryV1.Mutations.useUpdateSummaryFeedbackMutation({
      options: {
        onSuccess: () => {
          alert.success(t('Feedback submitted. Thank you!'));
          handleModalClose();
        },
        onError: () => {
          alert.error(t('Failed to submit feedback. Please try again.'));
        },
      },
    });

  const handleSubmitAdditionalFeedback = () => {
    const req = {
      id: feedbackRequest?.id ?? '',
      locationId: feedbackRequest?.locationId ?? '',
      feedbackType: feedbackRequest?.feedbackType ?? SummaryFeedbackType.POSITIVE,
      feedback: values.feedback ?? '',
      allowContactBack: values.allowContact,
    };

    submitFeedback(req);
    setFeedbackRequest(undefined);
  };

  return (
    <Modal {...feedbackModalProps} minWidth={480}>
      <Modal.Header onClose={handleModalClose}>{t('Provide Additional Feedback')}</Modal.Header>
      <Modal.Body>
        <Text
          css={{
            marginBottom: theme.spacing(2),
          }}
        >
          {feedbackRequest?.feedbackType === SummaryFeedbackType.POSITIVE
            ? t('Please share details about what you found valuable in the Weave AI Fax Overview.')
            : t('Please share details about your issues with the Weave AI Fax Overview.')}
        </Text>
        <FormRow>
          <TextareaField {...getFieldProps('feedback')} label={t('Feedback (Optional)')} />
        </FormRow>
        <FormRow>
          <CheckboxField {...getFieldProps('allowContact')} label={t('Allow Weave to contact me about my response')} />
        </FormRow>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Submit'),
          loading: isSubmittingFeedback,
          onClick: () => {
            handleSubmitAdditionalFeedback();
          },
        }}
      />
    </Modal>
  );
};
